import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { toast } from 'sonner';

const WebSocketContext = createContext(null);

export function useWebSocket() {
  return useContext(WebSocketContext);
}

export function WebSocketProvider({ children }) {
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const listenersRef = useRef(new Set());

  // Subscribe to raw WS messages - returns unsubscribe function
  const subscribe = useCallback((callback) => {
    listenersRef.current.add(callback);
    return () => listenersRef.current.delete(callback);
  }, []);

  const connect = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    if (wsRef.current && wsRef.current.readyState <= 1) return;

    const baseUrl = process.env.REACT_APP_BACKEND_URL;
    const wsUrl = baseUrl.replace(/^http/, 'ws') + '/api/ws?token=' + token;

    try {
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        setIsConnected(true);
        if (reconnectTimer.current) {
          clearTimeout(reconnectTimer.current);
          reconnectTimer.current = null;
        }
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          // Notify all subscribers
          listenersRef.current.forEach(cb => { try { cb(data); } catch {} });
          if (data.type === 'data_updated') {
            const ib = data.ib_name ? ` (${data.ib_name})` : '';
            toast.info(`${data.user_name} baru update ${data.page}${ib}`, {
              duration: 4000,
              position: 'bottom-right',
            });
          } else if (data.type === 'presence_update') {
            setOnlineUsers(data.online_users || []);
            if (data.action === 'joined') {
              toast(`${data.user?.name || 'Someone'} baru online`, {
                duration: 3000,
                position: 'bottom-right',
                icon: '🟢',
              });
            }
          }
        } catch {}
      };

      ws.onclose = () => {
        setIsConnected(false);
        wsRef.current = null;
        const t = localStorage.getItem('token');
        if (t) {
          reconnectTimer.current = setTimeout(connect, 5000);
        }
      };

      ws.onerror = () => {};

      wsRef.current = ws;
    } catch {}
  }, []);

  useEffect(() => {
    const checkToken = setInterval(() => {
      const token = localStorage.getItem('token');
      if (token && (!wsRef.current || wsRef.current.readyState > 1)) {
        connect();
      } else if (!token && wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
        setIsConnected(false);
        setOnlineUsers([]);
      }
    }, 2000);

    return () => {
      clearInterval(checkToken);
      if (wsRef.current) wsRef.current.close();
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
    };
  }, [connect]);

  return (
    <WebSocketContext.Provider value={{ isConnected, onlineUsers, subscribe }}>
      {children}
    </WebSocketContext.Provider>
  );
}
